import { useRef, useMemo } from 'react';

import { Card, CardHeader, CardTitle, CardContent } from '@mesh.ai/rabbit';
import { metricTypeTransformers } from 'Deer/hooks/dataHooks/metrics/utils/overviewMetricsUtils';
import { DeerLineChart } from 'Deer/pages/Team/DirectReports/DirectReportsOverview/cards/common/DeerLineChart';
import { useLocation } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { useGetEngagementSurveyReportsLineChart } from 'screens/Engagement/LandingPageV2/hooks/dataHooks';

import { NONE_ID, getSurveyDropdownValues } from './EngagementSurveyReportsGrid';
import {
  useSelectEngagementSelectedDateRange,
  useSelectEngagementSelectedSplitBy,
  useSelectEngagementSelectedViewBy,
  useSelectEngagementSurveys,
} from './slice/EngagementIntentSlice';

const EngagementSurveyReportLineChart = () => {
  const lineChartRef = useRef(null);
  const cohortDetails = useSelectedView();
  const engagementSurveys = useSelectEngagementSurveys();
  const selectedSplitBy = useSelectEngagementSelectedSplitBy();
  const selectedViewBy = useSelectEngagementSelectedViewBy();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const engagementSurveyId = engagementSurveys?.find(({ uuid }) => uuid === searchParams.get('surveyId'));

  const { value: selectedSplitById, label: selectedSplitByDisplayName } = selectedSplitBy;
  const { value: selectedViewById, label: selectedViewByDisplayName } = selectedViewBy;
  const timelineDateRange = useSelectEngagementSelectedDateRange();

  //   converting to timestamp
  const timelineRange = useMemo(
    () => ({
      startDate: timelineDateRange?.startDate?.getTime(),
      endDate: timelineDateRange?.endDate?.getTime(),
    }),
    [timelineDateRange]
  );

  const {
    isLoading,
    isError,
    data: res,
  } = useGetEngagementSurveyReportsLineChart({
    cohort: cohortDetails,
    feedbackFormIds: getSurveyDropdownValues({ engagementSurveyId, engagementSurveys }),
    // view by dropdown == group by
    rowGroupCols: [
      {
        id: selectedViewById,
        displayName: selectedViewByDisplayName,
        field: selectedViewById,
      },
    ],
    // split by dropdown == pivot
    pivotCols:
      selectedSplitById === NONE_ID
        ? []
        : [
            {
              id: selectedSplitById,
              displayName: selectedSplitByDisplayName,
              field: selectedSplitById,
            },
          ],
    pivotMode: true,
    selectedSplitById,
    dateRange: { startTs: timelineRange.startDate, endTs: timelineRange.endDate },
  });

  const lineChartData = useMemo(() => {
    const transformedData: { [key: string]: unknown } = {};

    // transformation of data will be done here based on metric type
    res?.entity?.metrics?.forEach((metric) => {
      const metricType = metric.metric;
      const dataTransformer = metricTypeTransformers[metricType];
      if (dataTransformer) {
        transformedData[metric.metric] = dataTransformer(metric, timelineDateRange);
      }
    });
    return transformedData ? transformedData.ENGAGEMENT_SCORE : {};
  }, [res?.entity?.metrics, timelineDateRange]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Proficiency</CardTitle>
      </CardHeader>
      <CardContent>
        <DeerLineChart
          isLoading={isLoading}
          isError={isError}
          ref={lineChartRef}
          nodes={lineChartData?.nodes ?? []}
          xAxisValues={lineChartData?.xAxis ?? []}
          series={lineChartData?.series ?? []}
          xAxisLabel="Time (Monthly)"
          yAxisLabel="Score"
          tooltipTitle="Progress"
          seriesSelectorTitle="Questions"
          preselectedNodes={10}
          hasNoData={!lineChartData?.series || lineChartData?.series?.length === 0}
          yAxisUnitSymbol={{
            symbol: '%',
            position: 'post',
          }}
          xAxisDomain={lineChartData?.xAxisDomain ?? []}
        />
      </CardContent>
    </Card>
  );
};

export { EngagementSurveyReportLineChart };
